import React, {useEffect, useState} from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { useOrganization } from "../../context/OrganizationContext";
import { hasPermission } from "../../utils/permissions";
import {useLocation} from "react-router";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { Alert, AlertTitle } from "@mui/material";

/**
 * PermissionProtectedRoute
 *
 * Protects routes by checking:
 * 1. User authentication
 * 2. Required permission in organization context
 *
 * @param {React.ReactNode} children - The component to render if permission is granted
 * @param {string} requiredPermission - The permission key from PERMISSIONS (e.g., 'CREATE_OPERATIONS')
 * @param {string} redirectTo - Where to redirect if permission is denied (default: '/dashboard')
 */
const PermissionProtectedRoute = ({
    children,
    requiredPermission,
    redirectTo = '/dashboard'
}) => {
    const { authenticated, token } = useAuth();
    const { isInOrganizationMode, userRole, loading: orgLoading } = useOrganization();
    const location = useLocation();
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            // Wait for next tick to ensure auth state is updated
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsReady(true);
        };
        checkAuth();
    }, [authenticated, token]);

    // Show loading spinner while checking auth
    if (!isReady || orgLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // Redirect to home if not authenticated
    if (!authenticated) {
        return <Navigate to="/home" state={{ from: location }} replace />;
    }

    // In organization mode, check permissions
    if (isInOrganizationMode && requiredPermission) {
        const userHasPermission = hasPermission(userRole, requiredPermission);

        if (!userHasPermission) {

            // Show access denied message and redirect
            return (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="100vh"
                    sx={{ p: 3 }}
                >
                    <Alert severity="warning" sx={{ maxWidth: 600 }}>
                        <AlertTitle>Access Denied</AlertTitle>
                        You do not have permission to access this feature. Your current role ({userRole}) does not include the required permission: {requiredPermission}.
                        <Box sx={{ mt: 2 }}>
                            <Navigate to={redirectTo} replace />
                        </Box>
                    </Alert>
                </Box>
            );
        }
    }

    // Permission granted or not in organization mode (individual users)
    return children;
};

export default PermissionProtectedRoute;
